import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import {
  activateCreditBuilder,
  getActiveCreditBuildings,
  getEquifaxSubscribedBuildings,
  getFAQs,
  getTenantDetails,
  unsubscribeCreditBuilder,
  updateBuilding,
} from '../../auth/_requests';
import { useAuth } from '../../auth/Auth';
import { useLeaseUpload } from '../../helpers/hooks/useLeaseUpload';
import { usePoints } from '../../helpers/hooks/pointsContext';
import { useModal } from '../../helpers/hooks/modalContext';
import { showToast } from '../../helpers/utils/showToast';
import { formatApiErrorMessage } from '../../helpers/utils/error';
import { useNavigate } from 'react-router-dom';
import Modal from '../../helpers/Modal/Modal';
import { AlertTriangle } from 'lucide-react';
import { ButtonSpinner } from '../../helpers/UI/ButtonSpinner/ButtonSpinner';
import {
  activateCreditBuilderBody,
  EquifaxEligibleBuilding,
  EquifaxSubscribedBuilding,
  FAQResponse,
  tenantDetails,
} from '../../helpers/utils/types';
import { InlineAlert } from '../../helpers/UI/InlineAlert/InlineAlert';
import {
  FetchAddress,
  formatDateToDisplayUTC,
  keepAlphanumericAndSpace,
  standardizeDate,
} from '../../helpers/utils/utils';
import StepFooter from './components/StepFooter';
import EnableStep from './components/EnableStep';
import TermsStep from './components/TermsStep';
import HowItWorksStep from './components/HowItWorksStep';
import UploadLeaseStep from './components/UploadLeaseStep';
import { RentReportingFormValues } from './types';
import { validateEquifaxSubscriptionEligibility } from '../../helpers/utils/equifaxValidation';

const CANADIAN_PROVINCES: { code: string; name: string }[] = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' },
];

const slideVariants = {
  enter: {
    y: -20,
    opacity: 0,
  },
  center: {
    y: 0,
    opacity: 1,
  },
  exit: {
    y: 20,
    opacity: 0,
    scale: 0.98,
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] as const },
  },
};

const RentReporting = () => {
  const { auth } = useAuth();
  const { refreshPoints } = usePoints();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState('');
  const [fileName, setFileName] = useState<string | undefined>('');
  const [docUrl, setdocumentUrl] = useState('');
  const [properties, setProperties] = useState<EquifaxEligibleBuilding[]>([]);
  const [subscribedBuildings, setSubscribedBuildings] = useState<EquifaxSubscribedBuilding[]>([]);
  const [propertySelectedId, setPropertySelectedId] = useState<string>('');
  const [loadingProperties, setLoadingProperties] = useState<boolean>(false);
  const [tenant, setTenant] = useState<tenantDetails>();
  const [activating, setActivating] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [loadingTenantDetails, setLoadingTenantDetails] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isManualAddress, setIsManualAddress] = useState(false);
  const [isAutoCompleteOpen, setIsAutoCompleteOpen] = useState(false);
  const [creditHealthFaqs, setCreditHealthFaqs] = useState<FAQResponse[]>([]);
  const [openFaqIds, setOpenFaqIds] = useState<Record<string, boolean>>({});
  const addressInputRef = useRef<HTMLInputElement | null>(null);

  const toggleFaq = (faqId: string) => {
    setOpenFaqIds((prev) => ({ ...prev, [faqId]: !prev[faqId] }));
  };

  const formik = useFormik<RentReportingFormValues>({
    initialValues: {
      leaseStart: '',
      leaseEnd: '',
      address: '',
      house: '',
      street: '',
      city: '',
      province: '',
      provinceCode: '',
      country: 'Canada',
      countryCode: 'CA',
      postal: '',
      unitNumber: '',
    },
    validationSchema: Yup.object({
      address: Yup.string().required('Address is required'),
      house: Yup.string().required('Building/Street number is required'),
      street: Yup.string().required('Street name is required'),
      city: Yup.string().required('City is required'),
      province: Yup.string().required('Province is required'),
      provinceCode: Yup.string().required('Province Code is required'),
      postal: Yup.string().required('Postal code is required'),
      unitNumber: Yup.string().required('Unit number is required'),
    }),
    onSubmit: () => {
      // submit is handled in handleSubmit
    },
  });

  useEffect(() => {
    const field = 'house';
    const value = formik.values[field];

    formik.validateField(field).then(() => {
      const error = formik.errors[field];
      if (!error && value) {
        formik.setFieldTouched(field, true, false);
      }
    });
  }, [currentStep]);

  const {
    isUploading,
    progress: uploadProgress,
    uploadError,
    handleFileChange,
  } = useLeaseUpload({
    token: auth?.data.token,
    onUploaded: ({ url, downloadUrl, fileName: uploadedName }) => {
      setFileName(uploadedName);
      setdocumentUrl(downloadUrl);
      setSelectedFile(url);
    },
  });

  useEffect(() => {
    const selectedProperty = properties.find((p) => p.tenantBuildingId === propertySelectedId);
    setSelectedFile(selectedProperty?.leaseDocumentUrl || '');
    setFileName(
      selectedProperty?.leaseDocumentUrl ? selectedProperty?.leaseDocumentUrl.split('/').pop() : ''
    );
    const formattedDate = selectedProperty?.leaseStartDate
      ? formatDateToDisplayUTC(selectedProperty.leaseStartDate)
      : '';
    const parsedEndDateFormatted = selectedProperty?.leaseEndDate
      ? formatDateToDisplayUTC(selectedProperty.leaseEndDate)
      : '';
    // Fall back to the address already saved on the tenant's profile so they don't retype it
    const profileAddress = selectedProperty ? tenant?.address : undefined;
    formik.setValues({
      leaseStart: formattedDate ?? '',
      leaseEnd: parsedEndDateFormatted ?? '',
      address: selectedProperty?.fullAddress || profileAddress?.fullAddress || '',
      house: selectedProperty?.buildingNumber || profileAddress?.buildingNumber || '',
      street: selectedProperty?.streetName || profileAddress?.streetName || '',
      city: selectedProperty?.city || profileAddress?.city || '',
      province: selectedProperty?.province || profileAddress?.province || '',
      provinceCode: selectedProperty?.provinceCode || profileAddress?.provinceCode || '',
      country: selectedProperty?.country || profileAddress?.country || 'Canada',
      countryCode: selectedProperty?.countryCode || profileAddress?.countryCode || 'CA',
      postal: selectedProperty?.postalCode || profileAddress?.postalCode || '',
      unitNumber: selectedProperty?.unitNumber || profileAddress?.unitNumber || '',
    });
    setStartDate(
      selectedProperty?.leaseStartDate ? new Date(selectedProperty.leaseStartDate) : null
    );
    setEndDate(selectedProperty?.leaseEndDate ? new Date(selectedProperty.leaseEndDate) : null);
    setdocumentUrl(selectedProperty?.leaseDocumentDownloadUrl || '');
  }, [propertySelectedId, properties, tenant]);

  const openDocument = () => {
    if (selectedFile) {
      window.open(docUrl, '_blank');
    }
  };

  const handlePropertySelect = (id: string) => {
    const newValue = propertySelectedId === id ? '' : id;
    setPropertySelectedId(newValue);
  };

  const fetchTenantDetails = async () => {
    try {
      setLoadingTenantDetails(true);
      const response = await getTenantDetails(auth?.data.token);
      setTenant(response.data);
    } catch (error) {
      console.error('Error fetching tenant details:', error);
    } finally {
      setLoadingTenantDetails(false);
    }
  };

  const handleSubmit = async () => {
    const validation = validateEquifaxSubscriptionEligibility(tenant);
    if (!validation.isValid) {
      if (validation.errorType === 'idv_required') {
        showToast({
          message: validation.errorMessage || 'Please complete Identity Verification (IDV) first.',
          type: 'error',
        });
        setModal('identity-verification');
      } else if (validation.errorType === 'profile_incomplete') {
        showToast({
          message: validation.errorMessage || 'Please complete your profile before subscribing.',
          type: 'error',
        });
      }
      return;
    }

    const requestBodyBuilding = {
      buildingDetails: {
        leaseDocumentUrl: selectedFile,
        leaseStartDate: startDate ? standardizeDate(formik.values.leaseStart) : '',
        leaseEndDate: endDate ? standardizeDate(formik.values.leaseEnd) : '',
      },
      addressDetails: {
        fullAddress: formik.values.address || '',
        streetName: formik.values.street || '',
        city: formik.values.city || '',
        province: formik.values.province || '',
        provinceCode: formik.values.provinceCode || '',
        postalCode: formik.values.postal || '',
        buildingNumber: formik.values.house || '',
        unitNumber: formik.values.unitNumber || '',
      },
    };
    const requestBody: activateCreditBuilderBody = {
      surname: tenant?.user.lastName || '',
      firstName: tenant?.user.firstName || '',
      middleName: tenant?.user.middleName || '',
      dateBirth: tenant?.user.dateOfBirth || '',
      telephoneNumber: tenant?.user.phoneNumber || '',
      address: {
        fullAddress: keepAlphanumericAndSpace(formik.values.address) || '',
        streetName: keepAlphanumericAndSpace(formik.values.street) || '',
        city: keepAlphanumericAndSpace(formik.values.city) || '',
        province: keepAlphanumericAndSpace(formik.values.province) || '',
        provinceCode: keepAlphanumericAndSpace(formik.values.provinceCode) || '',
        country: keepAlphanumericAndSpace(formik.values.country) || '',
        countryCode: keepAlphanumericAndSpace(formik.values.countryCode || 'CA'),
        postalCode: keepAlphanumericAndSpace(formik.values.postal) || '',
        buildingNumber: keepAlphanumericAndSpace(formik.values.house) || '',
        unitNumber: keepAlphanumericAndSpace(formik.values.unitNumber) || '',
      },
    };
    try {
      setActivating(true);
      await updateBuilding(auth?.data.token, propertySelectedId, requestBodyBuilding);
      const response = await activateCreditBuilder(
        auth?.data.token,
        propertySelectedId,
        requestBody
      );
      if (response.message === 'success') {
        await refreshPoints();
        showToast({
          message: 'Credit Builder has been successfully activated for the selected property!',
          type: 'success',
        });
        setTimeout(() => {
          navigate('/dashboard');
        }, 7000);
      } else if (response.message === 'already_subscribed') {
        void refreshPoints().catch((err) => console.error('Failed to refresh points', err));
        showToast({
          message: 'Credit Builder is already active for this property.',
          type: 'success',
        });
        setTimeout(() => {
          navigate('/dashboard');
        }, 7000);
      } else {
        throw new Error(
          formatApiErrorMessage(response.message, 'Failed to activate Credit Builder reporting. Please try again.')
        );
      }
    } catch (error) {
      const msg =
        error instanceof Error
          ? error.message
          : 'Failed to activate Credit Builder reporting. Please try again.';
      setServerError(msg);
      showToast({
        message: msg,
        type: 'error',
      });
    } finally {
      setActivating(false);
    }
  };

  const fetchAllBuildings = async () => {
    setLoadingProperties(true);
    try {
      const [eligibleRes, subscribedRes] = await Promise.all([
        getActiveCreditBuildings(auth?.data.token),
        getEquifaxSubscribedBuildings(auth?.data.token, 1, 50),
      ]);
      if (eligibleRes?.data) setProperties(eligibleRes.data);
      if (subscribedRes?.data) setSubscribedBuildings(subscribedRes.data);
    } catch (err) {
      console.error('Error fetching buildings:', err);
    } finally {
      setLoadingProperties(false);
    }
  };

  const { setModal, modalState } = useModal();
  const [isCancelling, setIsCancelling] = useState(false);

  const handleConfirmCancel = async () => {
    try {
      setIsCancelling(true);
      await unsubscribeCreditBuilder(auth?.data?.token, modalState?.tenantBuildingId || '');
      showToast({
        message: 'Credit Builder reporting has been successfully cancelled for this property.',
        type: 'success',
      });
      setModal(null);
      await fetchAllBuildings();
    } catch (error) {
      showToast({
        message:
          error instanceof Error
            ? error.message
            : 'Failed to cancel Credit Builder reporting. Please try again.',
        type: 'error',
      });
    } finally {
      setIsCancelling(false);
    }
  };

  useEffect(() => {
    fetchAllBuildings();
    fetchTenantDetails();
  }, [auth?.data?.token]);

  useEffect(() => {
    const getCreditBuilderFAQs = async () => {
      try {
        const response = await getFAQs(auth?.data?.token, 'creditBuilder');
        if (response) {
          setCreditHealthFaqs(response);
        } else {
          setCreditHealthFaqs([]);
        }
      } catch (error) {
        console.error('Error fetching credit builder FAQs:', error);
      }
    };
    getCreditBuilderFAQs();
  }, [auth?.data?.token]);

  const nextStep = () => {
    if (currentStep < 3) {
      setCurrentStep((prev) => prev + 1);
    }
  };
  const stepLabels = ['Enable', 'Terms', 'How it works', 'Upload'];
  const canSubmitActivation =
    !!selectedFile &&
    !!formik.values.leaseEnd &&
    !!formik.values.leaseStart &&
    !!formik.values.address &&
    !!formik.values.house &&
    !!formik.values.street &&
    !!formik.values.city &&
    !!formik.values.province &&
    !!formik.values.provinceCode &&
    !!formik.values.postal &&
    !!formik.values.unitNumber &&
    !isUploading &&
    !activating;

  const noEligibleBuildings = !!tenant?.user?.phoneNumber && properties.length === 0;
  const visibleTabLabels = noEligibleBuildings ? ['Enable'] : stepLabels;

  const steps = [
    {
      component: (
        <EnableStep
          properties={properties}
          propertySelectedId={propertySelectedId}
          onSelectProperty={handlePropertySelect}
        />
      ),
    },
    { component: <TermsStep /> },
    { component: <HowItWorksStep /> },
    {
      component: (
        <UploadLeaseStep
          formik={formik}
          fileInputRef={fileInputRef}
          selectedFile={selectedFile}
          fileName={fileName}
          isUploading={isUploading}
          uploadProgress={uploadProgress}
          uploadError={uploadError}
          openDocument={openDocument}
          onFileChange={handleFileChange}
          onClearFile={() => {
            setSelectedFile('');
            setFileName('');
          }}
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          isManualAddress={isManualAddress}
          setIsManualAddress={setIsManualAddress}
          addressInputRef={addressInputRef}
          canadianProvinces={CANADIAN_PROVINCES}
        />
      ),
    },
  ];

  if (loadingProperties || loadingTenantDetails) {
    return (
      <div className='flex items-center justify-center h-full'>
        <div className='w-8 h-8 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin' />
      </div>
    );
  }

  return (
    <section className='w-full'>
      <motion.div
        className='pg'
        id='pg-reporting'
        initial='hidden'
        animate='show'
        variants={{
          hidden: {},
          show: { transition: { staggerChildren: 0.12, delayChildren: 0.02 } },
        }}
      >
        <motion.div className='hero' variants={fadeUp}>
          <div className='pg-hero'>
            <div className='hero-beam' aria-hidden />
            <div className='orb-green-p' aria-hidden />
            <div className='orb-ice-p' aria-hidden />
            <div className='relative z-[1] px-5 py-6 sm:px-8 sm:py-7'>
              <div className='pg-hero-eye'>Credit building</div>
              <div className='pg-hero-title'>Rent reporting</div>
              <div className='pg-hero-sub'>
                Every on-time rent payment reported directly to Equifax. Free. Every month.
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div className='rr-tabs' variants={fadeUp}>
          {visibleTabLabels.map((label, i) => (
            <div
              key={label}
              className={`rt${noEligibleBuildings || i === currentStep ? ' on' : ''}`}
            >
              {label}
            </div>
          ))}
        </motion.div>

        <motion.div className='card' variants={fadeUp}>
          {tenant?.user?.phoneNumber ? (
            subscribedBuildings.length > 0 || properties.length > 0 ? (
              <div className='flex flex-col gap-6'>
                {/* Subscribed Buildings Section */}
                {subscribedBuildings.length > 0 && (
                  <div className='flex flex-col gap-3 p-8'>
                    <div className='text-main text-[18px] font-semibold'>Active rent reporting</div>
                    <div className='text-[#6B7285] text-[13px]'>
                      Equifax credit reporting is active for the following properties.
                    </div>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mt-2'>
                      {subscribedBuildings.map((building) => (
                        <div
                          key={building.tenantBuildingId}
                          className='rounded-[16px] border border-[#E8EAF0] bg-white p-5 shadow-[0px_8px_20px_rgba(17,24,39,0.05)] flex flex-col gap-3'
                        >
                          <div className='flex items-center gap-2'>
                            <div className='w-2 h-2 rounded-full bg-green-500 shrink-0' />
                            <span className='text-main font-semibold text-base'>{building.name}</span>
                          </div>
                          <div className='text-[#6B7285] text-sm'>
                            Rent amount: <span className='font-medium text-main'>CA${building.rentAmount}</span>
                          </div>
                          <button
                            type='button'
                            onClick={() => setModal('cancel-rent-reporting', { tenantBuildingId: building.tenantBuildingId })}
                            className='mt-1 rounded-[10px] border border-red-200 bg-red-50 text-red-600 text-sm font-medium py-2 px-4 hover:bg-red-100 transition-colors'
                          >
                            Cancel reporting
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Eligible Buildings — Onboarding wizard */}
                {properties.length > 0 && (
                  <div className='flex flex-col h-full'>
                    <div className={`mt-10 ${isAutoCompleteOpen ? 'overflow-hidden' : ''}`}>
                      {currentStep === 3 && (
                        <FetchAddress
                          addressInputRef={addressInputRef}
                          formik={formik}
                          isSaveManually={setIsManualAddress}
                          setIsAutocompleteOpen={setIsAutoCompleteOpen}
                        />
                      )}
                      <AnimatePresence mode='wait' custom={currentStep}>
                        <motion.div
                          key={currentStep}
                          custom={currentStep}
                          variants={slideVariants}
                          initial='enter'
                          animate='center'
                          exit='exit'
                          transition={{ duration: 0.3 }}
                          className='w-full'
                        >
                          {steps[currentStep].component}
                        </motion.div>
                      </AnimatePresence>
                    </div>

                    {serverError && (
                      <div className='px-8 mt-2 shrink-0'>
                        <InlineAlert
                          type='error'
                          message={serverError}
                          dismissible
                          onDismiss={() => setServerError('')}
                        />
                      </div>
                    )}

                    <div className='p-6 shrink-0'>
                      <StepFooter
                        currentStep={currentStep}
                        totalSteps={steps.length}
                        activating={activating}
                        canProceedFromFirstStep={!!propertySelectedId}
                        canSubmitActivation={canSubmitActivation}
                        onBack={() => setCurrentStep((prev) => Math.max(prev - 1, 0))}
                        onNext={nextStep}
                        onSubmit={handleSubmit}
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className='rr-empty'>
                <div className='rr-ic' style={{ color: '#9CA3AF' }}>
                  <svg
                    width='14'
                    height='14'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    aria-hidden
                  >
                    <path d='M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z' />
                    <line x1='12' y1='9' x2='12' y2='13' />
                    <line x1='12' y1='17' x2='12.01' y2='17' />
                  </svg>
                </div>
                <div className='rr-title'>Set up a recurring payment first</div>
                <div className='rr-body'>
                  To activate rent reporting, you must have at least one property with an active
                  recurring payment. Go to Pay rent and set up a recurring payment to proceed.
                  <br />
                  <br />
                  Note: you cannot resubscribe to a property that was previously unsubscribed from
                  rent reporting.
                </div>
                <button
                  type='button'
                  className='btn-blue btn-blue-lg'
                  onClick={() => navigate('/pay-rent')}
                >
                  Go to pay rent →
                </button>
              </div>
            )
          ) : (
            <div className='rr-empty'>
              <div className='rr-ic' style={{ color: '#9CA3AF' }}>
                <svg
                  width='14'
                  height='14'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  aria-hidden
                >
                  <path d='M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z' />
                  <line x1='12' y1='9' x2='12' y2='13' />
                  <line x1='12' y1='17' x2='12.01' y2='17' />
                </svg>
              </div>
              <div className='rr-title'>
                To start Credit Builder, please verify your phone number in your profile.
              </div>
              <div className='rr-body'>
                Go to your profile settings and verify your phone number to continue.
              </div>
              <button
                type='button'
                className='btn-blue btn-blue-lg'
                onClick={() => navigate('/profile')}
              >
                Go to profile →
              </button>
            </div>
          )}
        </motion.div>

        <motion.div variants={fadeUp}>
          <div className='sec-hdr' style={{ marginBottom: 12 }}>
            <div className='sec-title'>FAQs: Equifax reporting</div>
          </div>
          <div className='card card-body'>
            {creditHealthFaqs.map((faq) => {
              const isOpen = !!openFaqIds[faq.faqId];
              return (
                <div key={faq.faqId} className={`faq-item${isOpen ? ' open' : ''}`}>
                  <button
                    type='button'
                    className='faq-q'
                    onClick={() => toggleFaq(faq.faqId)}
                  >
                    {faq.question}
                    <span className='faq-plus'>+</span>
                  </button>
                  <div className='faq-a'>{faq.answer}</div>
                </div>
              );
            })}
          </div>
        </motion.div>

        <Modal id='cancel-rent-reporting' closeOnClickAway={false}>
          <div className='relative w-[370px] bg-[#FFF] rounded-[16px] flex flex-col p-8 justify-center items-center shadow-[0_20px_50px_rgba(0,0,0,0.15)] border border-[#E9EDF9]'>
            <div className='w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-4'>
              <AlertTriangle className='text-red-500 w-6 h-6' />
            </div>

            <h2 className='text-main font-semibold text-lg text-center mb-2'>
              Cancel rent reporting
            </h2>

            <p className='text-[#6B7280] text-sm text-center mb-6 leading-relaxed px-2'>
              Are you sure you want to cancel Equifax credit reporting for this building? <strong>You will not be able to re-subscribe this property again.</strong>
            </p>

            <div className='flex gap-3 w-full mt-2'>
              <button
                className='cancelBtn w-full py-2.5 rounded-[10px] text-sm font-medium border border-[#E5E7EB] hover:bg-[#F9FAFB] transition-colors'
                onClick={() => setModal(null)}
                disabled={isCancelling}
              >
                Go back
              </button>
              <button
                className='submitBtn w-full py-2.5 rounded-[10px] text-sm font-medium bg-[#0055FF] text-white hover:bg-[#0044CC] transition-colors flex items-center justify-center gap-2'
                onClick={handleConfirmCancel}
                disabled={isCancelling}
              >
                {isCancelling && <ButtonSpinner />}
                Confirm cancel
              </button>
            </div>
          </div>
        </Modal>
      </motion.div>
    </section>
  );
};

export default RentReporting;
